// Выбор филиалов в шапке — план 106 (заказ владельца 26.08).
//
// Было: один филиал или «Все». Мурманск — это ДВА филиала одного города, и
// менеджеру, чтобы видеть город целиком, приходилось либо переключаться между
// ними, либо ставить «Все филиалы» и получать вперемешку Москву. Отдельный
// фильтр «Мурманск» владелец отверг как костыль: «сделаем нормально —
// чекбоксами, и это будет работать везде, где есть разделение на филиалы».
//
// Пустой выбор = «Все филиалы»: та же договорённость, что на сервере, где
// пустой список филиалов значит «без ограничений».
import { Check, ChevronDown } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { branchSelectionLabel } from '@/lib/branch-label';
import { cn } from '@/lib/utils';

export interface BranchOption {
  id: string;
  name: string;
  shortName?: string | null;
  cityId: string;
}

interface Props {
  branches: BranchOption[];
  cityNames: Map<string, string>;
  selected: string[];
  onChange: (branchIds: string[]) => void;
}

/** Состояние чекбокса города: все его филиалы, часть или ни одного. */
function cityState(cityBranchIds: string[], selected: string[]): 'all' | 'some' | 'none' {
  const chosen = cityBranchIds.filter((id) => selected.includes(id)).length;
  if (chosen === 0) return 'none';
  return chosen === cityBranchIds.length ? 'all' : 'some';
}

export function BranchMultiSelect({ branches, cityNames, selected, onChange }: Props): JSX.Element {
  const [open, setOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent): void {
      if (!boxRef.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  // Города в порядке появления филиалов: у школы их два-три, сортировать нечего.
  const cities: Array<{ id: string; branches: BranchOption[] }> = [];
  for (const b of branches) {
    const found = cities.find((c) => c.id === b.cityId);
    if (found) found.branches.push(b);
    else cities.push({ id: b.cityId, branches: [b] });
  }
  // Один город — заголовок не показываем: лишний уровень там, где выбирать не из
  // чего, только мешает (у большинства школ город один).
  const showCities = cities.length > 1;

  const selectedBranches = selected
    .map((id) => branches.find((b) => b.id === id))
    .filter((b): b is BranchOption => Boolean(b));

  function toggleBranch(id: string): void {
    onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  }

  function toggleCity(cityBranchIds: string[]): void {
    const state = cityState(cityBranchIds, selected);
    onChange(
      state === 'all'
        ? selected.filter((id) => !cityBranchIds.includes(id))
        : [...new Set([...selected, ...cityBranchIds])],
    );
  }

  return (
    <div ref={boxRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-label="Выбор филиалов"
        title={selectedBranches.map((b) => b.name).join(', ') || 'Все филиалы'}
        className="inline-flex h-9 items-center gap-2 rounded-full bg-whisper-white px-4 text-sm font-medium text-ink-black outline-none focus-visible:ring-2 focus-visible:ring-blueprint-blue/40"
      >
        <span className="text-muted-text">Филиал:</span>
        <span className="max-w-[16rem] truncate">{branchSelectionLabel(selectedBranches)}</span>
        <ChevronDown className={cn('h-4 w-4 transition-transform', open && 'rotate-180')} />
      </button>

      {open ? (
        <div className="absolute left-0 top-full z-40 mt-2 w-80 rounded-2xl border border-divider-gray bg-canvas-white p-2 shadow-lg">
          {/* «Все филиалы» — это снятие всех отметок. Отдельным пунктом, потому
              что снимать их по одной в двух городах утомительно. */}
          <button
            type="button"
            onClick={() => onChange([])}
            className={cn(
              'flex w-full items-center justify-between rounded-lg px-2 py-2 text-left text-sm hover:bg-whisper-white',
              selected.length === 0 ? 'font-medium text-ink-black' : 'text-muted-text',
            )}
          >
            Все филиалы
            {selected.length === 0 ? <Check className="h-4 w-4 text-blueprint-blue" /> : null}
          </button>

          <div className="mt-1 max-h-80 overflow-y-auto">
            {cities.map((city) => {
              const ids = city.branches.map((b) => b.id);
              const state = cityState(ids, selected);
              return (
                <div key={city.id} className="mb-1">
                  {showCities ? (
                    <label className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-xs font-medium uppercase tracking-wide text-muted-text hover:bg-whisper-white">
                      <input
                        type="checkbox"
                        checked={state === 'all'}
                        ref={(el) => {
                          // «Часть филиалов города» — третье состояние чекбокса:
                          // иначе город с одним отмеченным залом выглядит как
                          // невыбранный вовсе.
                          if (el) el.indeterminate = state === 'some';
                        }}
                        onChange={() => toggleCity(ids)}
                        className="h-3.5 w-3.5 cursor-pointer accent-blueprint-blue"
                      />
                      {cityNames.get(city.id) ?? 'Город не указан'}
                    </label>
                  ) : null}
                  {city.branches.map((b) => (
                    <label
                      key={b.id}
                      className={cn(
                        'flex cursor-pointer items-center gap-2 rounded-lg py-1.5 pr-2 text-sm text-ink-black hover:bg-whisper-white',
                        showCities ? 'pl-7' : 'pl-2',
                      )}
                    >
                      <input
                        type="checkbox"
                        checked={selected.includes(b.id)}
                        onChange={() => toggleBranch(b.id)}
                        className="h-3.5 w-3.5 cursor-pointer accent-blueprint-blue"
                      />
                      <span className="truncate" title={b.name}>
                        {b.shortName?.trim() || b.name}
                      </span>
                    </label>
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
}
